import { useState } from "react";
import { cn } from "@/lib/utils";
import { ChevronUp, ChevronDown, ChevronsUpDown } from "lucide-react";
import { EmptyState } from "./EmptyState";

const SortIcon = ({ direction }) => {
  if (!direction) return <ChevronsUpDown className="w-4 h-4 text-muted-text" />;
  return direction === 'asc' 
    ? <ChevronUp className="w-4 h-4 text-electric-ice-blue" />
    : <ChevronDown className="w-4 h-4 text-electric-ice-blue" />;
};

export const Table = ({ 
  columns = [],
  data = [],
  sortable = true,
  onSort,
  emptyState,
  className 
}) => {
  const [sortConfig, setSortConfig] = useState({ key: null, direction: null });
  
  const handleSort = (key) => {
    if (!sortable) return;
    
    let direction = 'asc';
    if (sortConfig.key === key) {
      if (sortConfig.direction === 'asc') direction = 'desc';
      else if (sortConfig.direction === 'desc') direction = null;
    }
    
    setSortConfig({ key: direction ? key : null, direction });
    onSort?.(key, direction);
  };
  
  if (data.length === 0) {
    return emptyState || (
      <EmptyState 
        title="No data available" 
        description="There's nothing to display yet."
      />
    );
  }
  
  return (
    <div 
      className={cn(
        "responsive-table bg-graphite rounded-xl border border-white/[0.06] overflow-hidden",
        className
      )}
      data-testid="table"
    >
      <div className="overflow-x-auto">
        <table className="w-full min-w-[600px]">
          <thead>
            <tr className="border-b border-white/[0.06]">
              {columns.map((column) => (
                <th
                  key={column.key}
                  onClick={() => column.sortable !== false && handleSort(column.key)}
                  className={cn(
                    "px-4 py-3 text-left text-sm font-medium text-soft-silver whitespace-nowrap",
                    "bg-obsidian/50",
                    column.sortable !== false && sortable && "cursor-pointer hover:text-cloud-white",
                    column.align === 'right' && "text-right",
                    column.align === 'center' && "text-center"
                  )}
                  style={{ width: column.width }}
                >
                  <div className={cn(
                    "flex items-center gap-2",
                    column.align === 'right' && "justify-end",
                    column.align === 'center' && "justify-center"
                  )}>
                    {column.label}
                    {column.sortable !== false && sortable && (
                      <SortIcon 
                        direction={sortConfig.key === column.key ? sortConfig.direction : null} 
                      />
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.map((row, rowIndex) => (
              <tr 
                key={row.id || rowIndex}
                className={cn(
                  "border-b border-white/[0.06] last:border-0",
                  "hover:bg-white/[0.02] transition-colors duration-150"
                )}
              >
                {columns.map((column) => (
                  <td
                    key={column.key}
                    className={cn(
                      "px-4 py-3 text-sm whitespace-nowrap",
                      column.mono ? "font-mono text-soft-silver metric-value" : "text-cloud-white",
                      column.align === 'right' && "text-right",
                      column.align === 'center' && "text-center"
                    )}
                  >
                    {column.render 
                      ? column.render(row[column.key], row) 
                      : row[column.key]
                    }
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default Table;
